import { Box, Button, Grid2, IconButton } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import CommonFileUploadHint from "../../../../shared/commonFileUploadInfo/CommonFileUploadInfo";
import { useToaster } from "../../../../../hooks/useToasterContext/useToasterContext";

// Multi-lingual support
import { t } from "i18next";
import { MAX_IMAGE_SIZE } from "./ConsentFormTypes";

const ConsentForm = ({
  setValidateStep,
  onSubmit,
  farmerData,
  onFileDownload,
}: any) => {
  // Manage consent form data
  const [selectedConsentForm, setSelectedConsentForm] = useState<File | null>(
    null,
  );
  const [selectedConsentFormName, setSelectedConsentFormName] =
    useState<string>("");

  // Manage consent form validation
  const [isConsentFormRequired, setIsConsentFormRequired] =
    useState<boolean>(false);

  // Toaster
  const { setShowToaster } = useToaster();

  // Use the useFormik hook to manage the form
  const formik = useFormik({
    initialValues: {
      consentFormId: null,
      selectedConsentForm: selectedConsentForm,
      selectedConsentFormName: selectedConsentFormName,
      selectedConsentFormSignedUrl: "",
    },
    onSubmit: (values) => {
      // Required validation
      // if (
      //   !formik.values.selectedConsentFormName ||
      //   formik.values.selectedConsentFormName === ""
      // ) {
      //   setIsConsentFormRequired(true);
      //   return;
      // }

      // File size validatoin
      if (
        selectedConsentForm &&
        selectedConsentForm.size &&
        selectedConsentForm.size >= 5 * 1024 * 1024
      ) {
        return;
      }
      const farmerData: any = { ...values };
      onSubmit(farmerData);
    },
  });

  // Set existing data
  useEffect(() => {
    // Edit case handling
    if (farmerData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        consentFormId: farmerData?.consentFormId || null,
        selectedConsentForm: farmerData?.selectedConsentForm || null,
        selectedConsentFormName: farmerData?.selectedConsentFormName,
        selectedConsentFormSignedUrl: farmerData?.selectedConsentFormSignedUrl,
      }));

      setSelectedConsentForm(farmerData?.selectedConsentForm);
      setSelectedConsentFormName(farmerData?.selectedConsentFormName);
    }
  }, []);

  useEffect(() => {
    if (setValidateStep) {
      setValidateStep(() => formik.submitForm);
    }
  }, [formik.submitForm, setValidateStep]);

  // Handle consent form selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Reset validation message
    setIsConsentFormRequired(false);

    const file = event.currentTarget.files?.[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE) {
        setShowToaster(
          true,
          t("addUpdateField.mediaFile.imageFileSizeLimit"),
          "error",
        );
        const fileInput = document.getElementById(
          "farmerConsentForm",
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
        return;
      }

      setSelectedConsentForm(file);
      setSelectedConsentFormName(file.name);

      formik.setFieldValue("selectedConsentForm", file);
      formik.setFieldValue("selectedConsentFormName", file.name);
    }
  };

  // Handle remove selected file
  const handleRemoveSelectedFile = () => {
    // Reset validation message
    setIsConsentFormRequired(false);

    setSelectedConsentForm(null);
    setSelectedConsentFormName("");

    formik.setFieldValue("selectedConsentForm", null);
    formik.setFieldValue("selectedConsentFormName", "");

    // Reset file input value
    const fileInput = document.getElementById(
      "farmerConsentForm",
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }

    if (farmerData && farmerData.consentFormId) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        consentFormId: null,
      }));
    }
  };

  // Handle download selected file
  const handleDownloadSelectedFile = () => {
    // Check if a selected file is available; if not, check if the consent form URL is available
    if (selectedConsentForm) {
      onFileDownload(selectedConsentForm, selectedConsentFormName);
    } else if (farmerData?.selectedConsentFormSignedUrl) {
      onFileDownload(
        farmerData.selectedConsentFormSignedUrl,
        farmerData.selectedConsentFormName,
      );
    }
  };

  return (
    <Box>
      {/* Form Container */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "farm.contrastText",
          maxWidth: "auto",
          height: "var(--AgentWebHeight-farmerstepper)",
          overflowY: "auto",
          pl: 5,
        }}
      >
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={6}>
            {(!selectedConsentFormName || selectedConsentFormName === "") && (
              <Button
                variant="contained"
                component="label"
                fullWidth
                color="fileuploadbtn"
                className="custom-fileupload-btn"
                startIcon={<i className="icon-upload"></i>}
                sx={{ boxShadow: "none", "&:hover": { boxShadow: "none" } }}
              >
                {t("addUpdateFarmer.uploadCosentForm")}
                <input
                  id="farmerConsentForm"
                  type="file"
                  accept=".pdf"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            )}

            {selectedConsentFormName && selectedConsentFormName !== "" && (
              <Box
                my={1}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  fontSize={"14px"}
                  className="withoutspace-text-wrap"
                  sx={{
                    cursor: "pointer",
                    color: "blue",
                    textDecoration: "underline",
                  }}
                  onClick={handleDownloadSelectedFile}
                >
                  {selectedConsentFormName}
                </Box>

                <IconButton
                  sx={{ marginRight: 1 }}
                  onClick={handleRemoveSelectedFile}
                >
                  <i className="icon-close" />
                </IconButton>
              </Box>
            )}
            {!selectedConsentFormName && (
              <CommonFileUploadHint
                supportedTypes={["PDF"]}
                maxFiles={1}
                isImageOrVideo={false}
              />
            )}
            {/* API Error Message */}
            {isConsentFormRequired && (
              <Box
                sx={{
                  color: "agricolors.error",
                  fontSize: "12px",
                  marginLeft: "14px",
                }}
              >
                {t("addUpdateFarmer.requiredConsentFormMessage")}
              </Box>
            )}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default ConsentForm;
